import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfiguratorNewsletterService, ContentDataByDataviewDTO, NewsletterDTO } from '@tj/tj-dmp-lib-api';
import { NewsletterSubscriptionNode } from '@tj/tj-dmp-lib-api/model/newsletterSubscriptionNode';
import { ChannelSubscriptionStatus } from '@tj/tj-dmp-lib-api/model/channelSubscriptionStatus';
import { MessageService } from 'primeng/api';

// One table row: a newsletter on one of its channels. The name cell spans the newsletter's channels.
interface ConsentRow {
  newsletter: NewsletterSubscriptionNode;
  channel: string;
  level: number;
  firstChannel: boolean;
  channelCount: number;
}

interface SubscriptionData {
  uuid: string;
  channel: string;
  subscriptionDate: any;
  unsubscriptionDate: any;
  status: boolean;
}

@Component({
    selector: 'dmp-dyn-raw-detail-consents',
    templateUrl: './dyn-raw-detail-consents.component.html',
    styleUrls: ['./dyn-raw-detail-consents.component.scss'],
    standalone: false
})
export class DynRawDetailConsentsComponent implements OnInit {
  @Input() tenant: string;
  @Input() personalId: number;
  @Input() partner: string;
  @Input() data: ContentDataByDataviewDTO;

  newsletters: NewsletterSubscriptionNode[] = [];

  // Newsletters tree flattened into table rows, children after their parent.
  consentRows: ConsentRow[] = [];

  constructor(
    private newsletterConfigService: ConfiguratorNewsletterService,
    private messageService: MessageService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loadNewsletters();
  }

  loadNewsletters(): void {
    if (!this.tenant || !this.partner || !this.data) {
      return;
    }

    // Extract newsletter subscription data from the already-loaded data
    const subscriptions = this.extractSubscriptions();

    // Fetch newsletter configurations from configurator service
    this.newsletterConfigService.getNewslettersUsingGET(this.tenant, 'FR', 'FR', this.partner).subscribe({
      next: (newsletterConfigs) => {
        // Build the hierarchical tree structure
        this.newsletters = this.buildParentSubscriptionNodes(newsletterConfigs, subscriptions);
        this.consentRows = this.toRows(this.newsletters, 0);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('view.details.consents.error.summary'),
          detail: this.translate.instant('view.details.consents.error.detail', {message: err.message})
        });
      }
    });
  }

  /**
   * Extract newsletter subscription data from the ContentDataByDataviewDTO
   */
  private extractSubscriptions(): SubscriptionData[] {
    const tablePrefix = 'personal_newsletter.';
    const subscriptionMaps: SubscriptionData[] = [];

    // Check if personal_newsletter data exists in dataSubQuery
    const personalNewsletterData = this.data?.dataSubQuery?.['personal_newsletter'];

    if (!personalNewsletterData || !personalNewsletterData.content) {
      return subscriptionMaps;
    }

    // Extract subscription information from each row
    for (const dataItem of personalNewsletterData.content) {
      const rows = dataItem.rows;

      if (rows && rows[tablePrefix + 'uuid']) {
        const subscription: SubscriptionData = {
          uuid: rows[tablePrefix + 'uuid'],
          channel: rows[tablePrefix + 'channel'],
          subscriptionDate: rows[tablePrefix + 'date_subscribe'],
          unsubscriptionDate: rows[tablePrefix + 'date_unsubscribe'],
          status: rows[tablePrefix + 'newsletter_registration_status']
        };

        if (subscription.uuid) {
          subscriptionMaps.push(subscription);
        }
      }
    }

    return subscriptionMaps;
  }

  /**
   * Build parent subscription nodes from newsletter configs and subscription data
   */
  private buildParentSubscriptionNodes(
    allNewsletters: NewsletterDTO[],
    subscriptions: SubscriptionData[]
  ): NewsletterSubscriptionNode[] {
    // Create index of newsletters by UUID for fast lookup
    const newslettersByUuid = new Map<string, NewsletterDTO>();
    allNewsletters.forEach(n => newslettersByUuid.set(n.uuid, n));

    const parentNodes: NewsletterSubscriptionNode[] = [];

    // For each top-level newsletter, create subscription node
    allNewsletters
      .filter(n => !this.isChildNewsletter(n.uuid, allNewsletters))
      .forEach(newsletter => {
        const node = this.buildNewsletterSubscriptionNode(newsletter, newslettersByUuid, subscriptions);
        parentNodes.push(node);
      });

    return parentNodes;
  }

  /**
   * Check if a newsletter is a child of another newsletter
   */
  private isChildNewsletter(uuid: string, allNewsletters: NewsletterDTO[]): boolean {
    return allNewsletters.some(n =>
      n.children && n.children.some(child => child.uuid === uuid)
    );
  }

  /**
   * Build a newsletter subscription node from newsletter config and subscriptions
   */
  private buildNewsletterSubscriptionNode(
    newsletter: NewsletterDTO,
    newslettersByUuid: Map<string, NewsletterDTO>,
    subscriptions: SubscriptionData[]
  ): NewsletterSubscriptionNode {
    const node: NewsletterSubscriptionNode = {
      uuid: newsletter.uuid,
      name: newsletter.name,
      channelStatuses: {},
      children: []
    };

    // Add channel statuses for each enabled channel
    if (newsletter.channelEnabled) {
      for (const channelId of newsletter.channelEnabled) {
        // Find subscription info for this newsletter and channel
        const subscriptionInfo = this.findSubscription(newsletter.uuid, channelId, subscriptions);

        if (subscriptionInfo) {
          const channelStatus: ChannelSubscriptionStatus = {
            subscribed: subscriptionInfo.status,
            subscriptionDate: subscriptionInfo.subscriptionDate ? subscriptionInfo.subscriptionDate.toString() : null,
            unsubscriptionDate: subscriptionInfo.unsubscriptionDate ? subscriptionInfo.unsubscriptionDate.toString() : null
          };
          node.channelStatuses[channelId] = channelStatus;
        } else {
          // No subscription found, default to not subscribed
          node.channelStatuses[channelId] = {
            subscribed: false,
            subscriptionDate: null,
            unsubscriptionDate: null
          };
        }
      }
    }

    // Recursively build children nodes
    if (newsletter.children && newsletter.children.length > 0) {
      for (const childNewsletter of newsletter.children) {
        const childNewsletterFull = newslettersByUuid.get(childNewsletter.uuid);
        if (childNewsletterFull) {
          const childNode = this.buildNewsletterSubscriptionNode(childNewsletterFull, newslettersByUuid, subscriptions);
          node.children.push(childNode);
        }
      }
    }

    return node;
  }

  /**
   * Find subscription info for a specific newsletter UUID and channel
   */
  private findSubscription(
    uuid: string,
    channel: string,
    subscriptions: SubscriptionData[]
  ): SubscriptionData | null {
    // personal_newsletter.channel est en majuscules (EMAIL, SMS), channelEnabled de la newsletter en minuscules
    return subscriptions.find(s => s.uuid === uuid && s.channel?.toLowerCase() === channel?.toLowerCase()) || null;
  }
  
  private toRows(newsletters: NewsletterSubscriptionNode[], level: number): ConsentRow[] {
    // reduce + concat rather than flatMap: the project's TypeScript lib predates ES2019.
    return (newsletters ?? []).reduce((rows: ConsentRow[], newsletter) => {
      const channels = this.getChannels(newsletter);
      const ownRows = channels.map((channel, index) => ({
        newsletter, channel, level, firstChannel: index === 0, channelCount: channels.length
      }));
      return rows.concat(ownRows, this.toRows(newsletter.children, level + 1));
    }, []);
  }

  /**
   * Récupère la liste des canaux disponibles pour une newsletter
   */
  getChannels(newsletter: NewsletterSubscriptionNode): string[] {
    if (!newsletter || !newsletter.channelStatuses) {
      return [];
    }
    return Object.keys(newsletter.channelStatuses);
  }
  
  /**
   * Vérifie si l'utilisateur est inscrit à une newsletter pour un canal donné
   */
  isSubscribed(newsletter: NewsletterSubscriptionNode, channel: string): boolean {
    if (!newsletter || !newsletter.channelStatuses || !newsletter.channelStatuses[channel]) {
      return false;
    }
    return newsletter.channelStatuses[channel].subscribed === true;
  }
  
  /**
   * Récupère la date d'inscription pour une newsletter et un canal
   */
  getSubscriptionDate(newsletter: NewsletterSubscriptionNode, channel: string): Date | null {
    if (!newsletter || !newsletter.channelStatuses || !newsletter.channelStatuses[channel]) {
      return null;
    }
    
    const date = newsletter.channelStatuses[channel].subscriptionDate;
    return date ? new Date(date) : null;
  }
  
  /**
   * Récupère la date de désinscription pour une newsletter et un canal
   */
  getUnsubscriptionDate(newsletter: NewsletterSubscriptionNode, channel: string): Date | null {
    if (!newsletter || !newsletter.channelStatuses || !newsletter.channelStatuses[channel]) {
      return null;
    }
    
    const date = newsletter.channelStatuses[channel].unsubscriptionDate;
    return date ? new Date(date) : null;
  }
}
